import { useState, useMemo, ReactElement, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress } from '@mui/material';

import { ReactComponent as MergeIcon } from 'images/icons/bulkActions/mergeIcon.svg';
import MessagePrompt from 'common/components/messagePrompt';
import MergeTicketsTable from 'features/mergeTickets/MergeTicketsTable';
import { ReactComponent as InfoIcon } from 'images/icons/info.svg';
import useAnalytics from 'common/utils/hooks/useAnalytics';
import { TRACK_EVENTS } from 'constants/trackEvents';
import useDisableActivePopup from 'common/utils/hooks/useDisableActivePopup';

import { selectIsPopupActive, ActivePopupName } from 'store/activePopups';
import MergeDetails from './MergeDetails';
import DontShowMeAgainPrompt from './DontShowMeAgainPrompt';
import useTexts from './hooks/useTexts';
import { clearMerge, selectIsMergeDisabled } from './slice';
import useHandleMerge from './hooks/useHandleMerge';
import { StyledText, StyledPromptTitle, StyledAsterisk, MergePromptTitleWrapper, StyledAnnotation } from './style';
import { StyledActionIcon, StyledActionTitle, StyledAction, CircularProgressWrapper } from '../../style';

const promptStyle = {
  promptWrapper: {
    overflowY: 'scroll',
    scrollbarWidth: 'thin',
    '*::-webkit-scrollbar': {
      width: '4px',
    },
    '*::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
    },
    '.dont-show-again-wrapper': {
      display: 'flex',
      gap: '4px',
      marginLeft: 0,
      marginRight: 'auto',
      fontSize: '12px',
    },
  },
};

const MergeAction = ({ dataset }: { dataset: Record<string, any> }): ReactElement => {
  const dispatch = useDispatch();
  const trackEvents = useAnalytics();

  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showWarningPrompt, setShowWarningPrompt] = useState(false);
  const [isMergeLoading, setMergeLoading] = useState(false);

  const isMergeButtonDisabled = useSelector(selectIsMergeDisabled) || isMergeLoading;
  const isMergeWarningPromptActive = useSelector(selectIsPopupActive(ActivePopupName.MERGE_CONFIRMATION));

  const disableMergeConfirmationPopup = useDisableActivePopup(ActivePopupName.MERGE_CONFIRMATION);

  const {
    mergeTicketsModalTitle,
    mergeTicketsModelSubTitle,
    mergeTicketsAnnotation,
    mergeTicketsText,
    mergeWarningConfirmBtn,
  } = useTexts();

  const handleOpenMergeModal = () => {
    trackEvents(TRACK_EVENTS.MERGE_CLICK_IN_BULK_ACTION_BAR);
    setShowMergeModal(true);
  };

  const handleCloseMergeModal = useCallback(() => {
    trackEvents(TRACK_EVENTS.CANCEL_CLICK_IN_SPECIFICATIONS_OVERLAY);
    dispatch(clearMerge());
    setShowMergeModal(false);
  }, [dispatch, trackEvents]);

  const handleClosePrompt = useCallback(() => {
    trackEvents(TRACK_EVENTS.CANCEL_CLICK_IN_MERGE_CONFIRMATION_POPUP);
    setShowWarningPrompt(false);
    dispatch(clearMerge());
  }, [trackEvents, dispatch]);

  const title = useMemo(
    () => (
      <MergePromptTitleWrapper>
        <StyledPromptTitle>
          {mergeTicketsModalTitle} <StyledAsterisk />
        </StyledPromptTitle>
      </MergePromptTitleWrapper>
    ),
    [mergeTicketsModalTitle],
  );

  const customAnnotation = useMemo(
    () => (
      <StyledAnnotation>
        <InfoIcon />
        {mergeTicketsAnnotation}
      </StyledAnnotation>
    ),
    [mergeTicketsAnnotation],
  );

  const handleMerge = useHandleMerge(setMergeLoading, handleCloseMergeModal, setShowWarningPrompt);

  const getButtonContentWithLoader = useCallback(
    (text: string) => {
      if (!isMergeLoading) {
        return text;
      }
      return (
        <CircularProgressWrapper>
          <CircularProgress size="1rem" />
        </CircularProgressWrapper>
      );
    },
    [isMergeLoading],
  );

  const mergeBtnStyle = useMemo(() => (isMergeLoading ? { padding: '6.5px 16px' } : null), [isMergeLoading]);

  const handleMergeClick = useCallback(() => {
    const dontShowWarningPrompt = !isMergeWarningPromptActive;
    trackEvents(TRACK_EVENTS.MERGE_CLICK_IN_SPECIFICATIONS_OVERLAY);
    if (dontShowWarningPrompt) {
      handleMerge();
    } else {
      setShowMergeModal(false);
      setShowWarningPrompt(true);
    }
  }, [handleMerge, trackEvents, isMergeWarningPromptActive]);

  const handlePromptOkClick = useCallback(
    async (isDontShowMeAgain: boolean) => {
      setMergeLoading(true);
      trackEvents(TRACK_EVENTS.GOT_IT_CLICK_IN_MERGE_CONFIRMATION_POPUP);

      if (isDontShowMeAgain) {
        await disableMergeConfirmationPopup();
      }
      handleMerge();
    },
    [handleMerge, trackEvents, disableMergeConfirmationPopup],
  );

  const mergeBtnText = useMemo(
    () => getButtonContentWithLoader(mergeTicketsText),
    [getButtonContentWithLoader, mergeTicketsText],
  );
  const promptBtnText = useMemo(
    () => getButtonContentWithLoader(mergeWarningConfirmBtn),
    [getButtonContentWithLoader, mergeWarningConfirmBtn],
  );

  return (
    <>
      {/* @ts-ignore */}
      <StyledAction isCursorPointer onClick={handleOpenMergeModal} {...dataset}>
        <StyledActionIcon>
          <MergeIcon />
        </StyledActionIcon>
        <StyledActionTitle>{mergeTicketsText}</StyledActionTitle>
      </StyledAction>
      {showMergeModal && (
        // @ts-ignore
        <MessagePrompt
          title={title}
          isFitContent
          showCancelBtn
          btnOkText={mergeBtnText}
          okBtnStyles={mergeBtnStyle}
          isOkButtonDisable={isMergeButtonDisabled}
          isCancelButtonDisable={isMergeLoading}
          open={showMergeModal}
          onClose={handleCloseMergeModal}
          onOkClick={handleMergeClick}
          showIcon={false}
          customAnnotation={customAnnotation}
          style={promptStyle}
        >
          <StyledText>{mergeTicketsModelSubTitle}</StyledText>
          <MergeTicketsTable />
          <MergeDetails />
        </MessagePrompt>
      )}
      <DontShowMeAgainPrompt
        showPrompt={showWarningPrompt}
        handleClosePrompt={handleClosePrompt}
        onOkClick={handlePromptOkClick}
        btnOkText={promptBtnText}
        mergeBtnStyle={mergeBtnStyle}
        isMergeLoading={isMergeLoading}
      />
    </>
  );
};

export default MergeAction;
